import { useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  ModalBody,
  ModalContent,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react'

import {
  Box as DarkBox,
  Modal as DarkModal,
  Tabs as DarkTabs,
} from '@hivewatch/dark'

import { Modal } from '@/components/overlays'
import { CaseModalHeader, CreateCaseForm } from '@/features/case-crud'

import { AssociateExisitingCaseForm } from './AssociateExisitingCaseForm'

interface AssociateCaseModalIProps {
  incidentId: string
  isOpen: boolean
  onClose: () => void
}

export const AssociateCaseModal = ({
  incidentId,
  isOpen,
  onClose,
}: AssociateCaseModalIProps) => {
  const { t } = useTranslation()
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const colorScheme = useLightMode()
  const [activeTab, setActiveTab] = useState('new')

  if (isRebrand) {
    const tabItems = [
      { id: 'new', label: t('incidents:new_case') },
      { id: 'existing', label: t('incidents:existing_case') },
    ]
    return (
      <DarkModal
        {...(colorScheme === 'dark' && {
          bodyStyleOverrides: { bg: 'bg.card' },
          headerStyleOverrides: { container: { bg: 'bg.card' } },
        })}
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveHidden
        onClose={onClose}
        title={t('incidents:associate_case_title')}
        zIndex={1500}
      >
        <DarkTabs
          activeId={activeTab}
          colorScheme={colorScheme}
          items={tabItems}
          onChange={setActiveTab}
        />
        <DarkBox mt='16px'>
          {activeTab === 'new' ? (
            <CreateCaseForm
              colorScheme={colorScheme}
              incidentId={incidentId}
              isRebrand
              onClose={onClose}
            />
          ) : (
            <AssociateExisitingCaseForm
              colorScheme={colorScheme}
              incidentId={incidentId}
              isRebrand
              onClose={onClose}
            />
          )}
        </DarkBox>
      </DarkModal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff'>
        <CaseModalHeader pb={0} title='Associate incident with a case' />

        <ModalBody pt={0} px={6} py={2}>
          <Tabs isFitted isLazy variant='primary'>
            <TabList>
              <Tab>New Case</Tab>
              <Tab>Existing Case</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <CreateCaseForm incidentId={incidentId} onClose={onClose} />
              </TabPanel>
              <TabPanel px={0}>
                <AssociateExisitingCaseForm
                  incidentId={incidentId}
                  onClose={onClose}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
